import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Popover,
  Typography,
  ButtonGroup,
  Button,
  Tooltip,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  SwapHorizontalCircle,
  History,
} from '@mui/icons-material';

import {
  setParams,
  resetOrderDetails,
  setSelectedSku,
  setReturnedOrder,
  resetSelectedSku,
  resetMovementData,
  resetHistoryData,
  resetReturnedOrder,
} from '../../store/slices/orderSlice';

import { deepOrange } from '@mui/material/colors';

import styled from '@emotion/styled';
import { IsRouteOpen, RegionCheck, getDateMinus21 } from '../../utils/helpers';
import orderServices from '../../services/orders';

import { CellCardLink, CellCardButton } from './CellItems/CellCardLink';
import CellLink from './CellItems/CellLink';
import ImgPopover from './CellItems/ImagePopover';
import ImageModal from './CellItems/ImageModal';
import OrderModal from '../../features/OrderModal/OrderModal';
import OrderMovementModal from '../../features/OrderMovementModal/OrderMovement';
import OrderHistorytModal from '../../features/OrderHistoryModal/OrderHistory';
import ReturnProductModal from '../../features/ReturnProductModal/ReturnProduct';
import ApiResponseModal from '../../features/ApiResponseModal/ApiResponse';

import defaultImage from '../../assets/images/img-default.gif';

const HighlightColor = {
  Complete: '#d6f5d6',
  Partial: '#f5eeb8',
  None: 'white',
}

const TableRowStyle = styled(TableRow)`
  .last-tr {
    border-right: none;
    border-bottom: none;
  }
`;

const TableHeader = styled(TableCell)`
  background-color: #d5e7f7;
  border-right: 1px solid #a3a3a3;
  padding: 0 1em;
`;

const TableCellRow = styled(TableCell)`
  border-right: 1px solid #a3a3a3;
  padding: 0 1em;
`;

const TableCellRowChild = styled(TableCell)`
  border-right: 1px solid #a3a3a3;
  padding: 0 1em;
  font-size: 1.15em;
`;

function outputImgUrl(assetId, profile) {
  if (assetId)
    return `https://caas-content.rtgbizapps.com/rendition?id=${assetId}`;
  if (profile)
    return `https://caas-content.rtgbizapps.com/rendition?id=${assetId}&prid=${profile}`;
  return defaultImage;
}

/** SAVE FOR LATER
 * <Checkbox
        checked={sku.checked}
        onChange={() => dispatch(updateSkuReturned({ rowId: row.OrderNumber1, skuId: sku.OrderNumber2 }))}
    />

    ---TableRow Items - RETURNS

    <TableHeader className='last-tr' align='center'>Returned</TableHeader>
    <TableCellRowChild align='center'></TableCellRowChild>
 */

function shopRepairCheck(orderNum) {
  return orderNum.toLowerCase() === 'r';
}

function NormalRow({ children }) {
  return (
    <TableRowStyle
      sx={{
        '& > *': { orderBottom: 'unset' },
      }}
    >
      {children}
    </TableRowStyle>
  );
}

function HighlightedRow({ children, handlePopoverOpen, handlePopoverClose }) {
  return (
    <TableRowStyle
      sx={{
        '& > *': { borderBottom: 'unset' },
        backgroundColor: deepOrange[100],
      }}
      onMouseEnter={handlePopoverOpen}
      onMouseLeave={handlePopoverClose}
    >
      {children}
    </TableRowStyle>
  );
}

export default function CollapsibleRow({ row, openOverride, searchProducts }) {
  const dispatch = useDispatch();
  const savedRegion = useSelector((state) => state.order.region);
  const savedParams = useSelector(
    (state) => state.product.previousSearchValues
  );
  const selectedReturnOrder = useSelector(
    (state) => state.order.selectedReturnOrder
  );
  const routeStatus = useSelector((state) => state.product.routeStatus);
  const employeeId = useSelector((state) => state.product.employeeId);
  const warehouse = useSelector((state) => state.product.warehouseId);
  const driverName = useSelector((state) => state.product.driverName);

  const { date, route } = savedParams;

  const [open, setOpen] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [storedModalInfo, setStoredModalInfo] = useState({
    skuNum: '',
    descr: '',
    src: '',
  });
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [openMoveModal, setOpenMoveModal] = useState(false);
  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [openReturnProductModal, setReturnProductModal] = useState(false);
  const [division, setDivision] = useState('FL');
  const [apiResponseModal, setApiResponseModal] = useState(false);
  const [apiResponseMessage, setApiResponseMessage] = useState('');
  const [apiResponseTitle, setApiResponseTitle] = useState('');

  const handleModalOpen = useCallback((sku, description, src) => {
    setStoredModalInfo({
      descr: description,
      src: src,
      skuNum: sku,
    });
    setModalOpen(true);
  }, []);
  const handleModalClose = useCallback(() => {
    setStoredModalInfo({
      skuNum: '',
      descr: '',
      src: '',
    });
    setModalOpen(false);
  }, []);

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const popoverOpen = Boolean(anchorEl);

  const handleOpenDetailsModal = (orderNum) => {
    const region = RegionCheck(savedRegion);
    dispatch(setParams({ orderNum: orderNum, region: region }));
    setOpenDetailsModal(true);
  };
  const handleCloseDetailsModal = () => {
    dispatch(resetOrderDetails());
    setOpenDetailsModal(false);
  };

  const handleOpenMovementModal = (sku, row) => {
    dispatch(resetMovementData());
    dispatch(
      setSelectedSku({
        orderNum: row.SalesOrderNumber,
        sku: sku.SkuNumber,
        pmkey: sku.PMKey,
        warehouseId: warehouse,
        division: savedParams.division,
        startDate: getDateMinus21().toString(),
        endDate: new Date().toString(),
      })
    );
    setOpenMoveModal(true);
  };

  const handleCloseMovementModal = () => {
    dispatch(resetSelectedSku());
    setOpenMoveModal(false);
  };

  const handleOpenHistoryModal = (sku, row) => {
    dispatch(resetHistoryData());
    dispatch(
      setSelectedSku({
        orderNum: row.SalesOrderNumber,
        sku: sku.SkuNumber,
        pmkey: sku.PMKey,
        warehouseId: warehouse,
        division: savedParams.division,
        startDate: getDateMinus21().toString(),
        endDate: new Date().toString(),
      })
    );
    setOpenHistoryModal(true);
  };

  const handleCloseHistoryModal = () => {
    dispatch(resetSelectedSku());
    setOpenHistoryModal(false);
  };

  const handleCloseReturnProductModal = () => {
    dispatch(resetReturnedOrder());
    setReturnProductModal(false);
  };

  const handleSelectedReturnProduct = (row) => {
    let returnLines = row.SkuData.map((route) => ({
      SkuNumber: route.SkuNumber,
      SkuDescription: route.VendorDescription,
      ReturnQuantity: 0,
      AcceptedQuantity: route.AcceptQuantity,
      OrderQuantity: route.OrderQuantity,
      Highlight: route.Highlight,
    }));

    if (row.SalesOrderNumber.endsWith('RT')) {
      returnLines = returnLines.filter(
        (line) => line.OrderQuantity < 0 && line.AcceptedQuantity <= 0
      );
    }

    if (
      row.SalesOrderNumber.endsWith('EX') &&
      !row.SalesOrderNumber.startsWith('R')
    ) {
        returnLines = returnLines.filter(
          (line) => line.OrderQuantity < 0 && line.AcceptedQuantity <= 0
        );
    }

    dispatch(
      setReturnedOrder({
        ...selectedReturnOrder,
        ...{
          OrderNumber: row.SalesOrderNumber,
          ReturnLines: returnLines,
        },
      })
    );
    setReturnProductModal(true);
  };

  const canReturn = (row) => {

    // If the order has an error message, then we can't return
    if (row.ErrorMessage) {
      return false;
    }

    const isEXOrder =
      row.SalesOrderNumber.endsWith('EX') &&
      !row.SalesOrderNumber.startsWith('R');
    const isRTOrder = row.SalesOrderNumber.endsWith('RT');
    const isNotSuccessful =
      row.DispatchCodeStatus === 2 || row.DispatchCodeStatus === 3;

    if (isEXOrder || isRTOrder) {
      const returnLines = row.SkuData.filter(
        (line) => line.OrderQuantity < 0 && line.AcceptQuantity <= 0
      );

      if (isEXOrder && isNotSuccessful) {
        return true;
      }

      return returnLines.length > 0;
    }

    return true;
  };

  const submitReturnProductChanges = async (
    returnLines,
    orderNumber,
    printer
  ) => {
    setLoading(true);
    const response = await orderServices.postReturnOrders(
      division,
      warehouse,
      employeeId,
      returnLines,
      orderNumber,
      route,
      printer,
      date
    );
    setLoading(false);
    if (typeof response.Value === 'string') {
      setApiResponseTitle('Error');
      setApiResponseMessage(response.Value);
    } else if (response.Value?.IsSuccessful) {
      setApiResponseTitle(`Return Status - ${response.Value.OrderNumber}`);
      const someNotPicked = returnLines.some(
        (line, index) =>
          response.Value.ReturnLines[index] &&
          !response.Value.ReturnLines[index].IsPicked
      );
      const lineItems = returnLines
        .map((line, index) => {
          const respLine = response.Value.ReturnLines[index];
          if (!respLine) return null;
          const skuLabel = respLine.SkuCorrection
            ? `${line.SkuNumber} (Corrected to: ${respLine.SkuCorrection})`
            : line.SkuNumber;
          const status = respLine.IsPicked
            ? 'Picked'
            : `Not Picked, Error: ${respLine.LineErrorMessage}`;
          return { label: `Sku: ${skuLabel}`, message: `Status: ${status}` };
        })
        .filter(Boolean);

      const sections = [];
      if (lineItems.length > 0) {
        sections.push({ title: 'Line Item Statuses', items: lineItems });
      }

      setApiResponseMessage({
        action: 'Return',
        success: true,
        errorText: [
          someNotPicked ? "Some lines weren't returned." : null,
          `The Order was ${response.Value.IsValidOrder ? 'Valid' : 'Invalid'}.`,
          response.Value.OrderErrorMessage ? `Order Error: ${response.Value.OrderErrorMessage}.` : null,
        ].filter(Boolean).join(' '),
        sections: sections.length > 0 ? sections : undefined,
      });
    } else {
      const lineErrorItems = returnLines
        .map((line, index) => {
          const respLine = response.Value.ReturnLines[index];
          if (!respLine) return null;
          return {
            label: `Sku: ${line.SkuNumber}`,
            message: respLine.LineErrorMessage || 'Server Error',
          };
        })
        .filter(Boolean);

      setApiResponseMessage({
        action: 'Return',
        success: false,
        errorText: `The Order was ${response.Value.IsValidOrder ? 'Valid' : 'Invalid'}. Order Error: ${response.Value.OrderErrorMessage}.`,
        sections: [{ title: 'Errors by Line Item', items: lineErrorItems }],
      });
    }

    setApiResponseModal(true);

    handleCloseReturnProductModal();
  };

  useMemo(() => {
    setOpen(openOverride);
  }, [openOverride]);

  useMemo(() => {
    setDivision(savedParams.division === null ? 'FL' : savedParams.division);
  }, [savedParams]);

  return (
    <React.Fragment>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {shopRepairCheck(row.SalesOrderNumber[0]) ? (
        <HighlightedRow
          handlePopoverOpen={handlePopoverOpen}
          handlePopoverClose={handlePopoverClose}
        >
          <TableCellRow>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCellRow>
          <TableCellRow component="th" scope="row">
            <CellCardButton
              modalFn={() => handleOpenDetailsModal(row.SalesOrderNumber)}
            >
              {row.SalesOrderNumber}
            </CellCardButton>
          </TableCellRow>
          <TableCellRow>{driverName}</TableCellRow>
          <TableCellRow>{row.CustomerName}</TableCellRow>
          <TableCellRow>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <CellCardLink
                href={
                  process.env.REACT_APP_DTLINK +
                  `service-orders/${division}${row.SalesOrderNumber}`
                }
                code={row.DispatchCodeStatus}
              >
                {row.DispatchCode}
              </CellCardLink>
              <Button
                onClick={() => handleSelectedReturnProduct(row)}
                disabled={!canReturn(row) || !IsRouteOpen(routeStatus)}
              >
                Return
              </Button>
            </Box>
          </TableCellRow>
          <Popover
            id="mouse-over-popover"
            sx={{
              pointerEvents: 'none',
            }}
            open={popoverOpen}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            onClose={handlePopoverClose}
            disableRestoreFocus
          >
            <Typography sx={{ p: '.5em 2em' }}>
              {'Shop Repair Order'}
            </Typography>
          </Popover>
        </HighlightedRow>
      ) : (
        <NormalRow>
          <TableCellRow>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCellRow>
          <TableCellRow component="th" scope="row">
            <CellCardButton
              modalFn={() => handleOpenDetailsModal(row.SalesOrderNumber)}
            >
              {row.SalesOrderNumber}
            </CellCardButton>
          </TableCellRow>
          <TableCellRow>{driverName}</TableCellRow>
          <TableCellRow>{row.CustomerName}</TableCellRow>
          <TableCellRow>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <CellCardLink
                href={
                  process.env.REACT_APP_DTLINK +
                  `service-orders/${division}${row.SalesOrderNumber}`
                }
                code={row.DispatchCodeStatus}
              >
                {row.DispatchCode}
              </CellCardLink>
              <Button
                onClick={() => handleSelectedReturnProduct(row)}
                disabled={!canReturn(row) || !IsRouteOpen(routeStatus)}
              >
                Return
              </Button>
            </Box>
          </TableCellRow>
        </NormalRow>
      )}

      <TableRow>
        <TableCell
          style={{
            paddingBottom: 0,
            paddingTop: 0,
            backgroundColor: '#e9e9e9',
          }}
          colSpan={6}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            {row.ErrorMessage && row.ErrorMessage.trim() !== '' ? (
              <Box
                sx={{
                  margin: 1,
                  padding: 2,
                  backgroundColor: '#f8d7da',
                  border: '1px solid #f5c2c7',
                  borderRadius: 1,
                }}
              >
                <Typography color="error" variant="body1">
                  {row.ErrorMessage}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ margin: 1 }}>
                <TableContainer component={Paper}>
                  <Table size="small" aria-label="associated SKUs">
                  <colgroup>
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '10%' }} />
                  </colgroup>
                  <TableHead>
                    <TableRowStyle>
                      <TableHeader align="center">Accepted Qty</TableHeader>
                      <TableHeader>SKU</TableHeader>
                      <TableHeader>Description</TableHeader>
                      <TableHeader>Vendor</TableHeader>
                      <TableHeader>Reference Color/Finish</TableHeader>
                      <TableHeader>Dispatch</TableHeader>
                      <TableHeader align="center">Actions</TableHeader>
                    </TableRowStyle>
                  </TableHead>
                  <TableBody>
                    {row.SkuData.map((sku, idx) => {
                      
                      const key = sku.SkuNumber
                        ? `${sku.SkuNumber}-${sku.OrderNumber2 ?? idx}`
                        : idx;
                      return (
                        <TableRow
                          key={key}
                          style={
                            { backgroundColor: HighlightColor[sku.Highlight] }
                          }
                        >
                          <TableCellRowChild
                            component="th"
                            scope="row"
                            align="center"
                          >
                            {sku.AcceptQuantity}
                          </TableCellRowChild>
                          <TableCellRowChild>
                            <Box
                              display="flex"
                              justifyContent="space-evenly"
                              alignItems="center"
                            >
                              <CellLink
                                href={
                                  process.env.REACT_APP_PDMLINK +
                                  `Product/Get/${sku.SkuNumber}/${division}`
                                }
                                sku={sku.SkuNumber}
                              >
                                {sku.SkuNumber}
                              </CellLink>
                              <ImgPopover
                                sku={sku.SkuNumber}
                                srcHref={outputImgUrl(sku.AssetId, '')}
                                description={sku.VendorDescription}
                                modalOpenFn={handleModalOpen}
                              />
                            </Box>
                          </TableCellRowChild>
                          <TableCellRowChild>
                            {sku.VendorDescription}
                          </TableCellRowChild>
                          <TableCellRowChild>
                            {sku.PMKey.substring(0, 4)}
                          </TableCellRowChild>
                          <TableCellRowChild>
                            {sku.ReferenceColorFinish.endsWith('/')
                              ? sku.ReferenceColorFinish.slice(0, -1)
                              : sku.ReferenceColorFinish}
                          </TableCellRowChild>
                          <TableCellRowChild>
                            {`Qty: ${
                              sku.OrderQuantity ? sku.OrderQuantity : 0
                            } Delivered: ${
                              sku.AcceptQuantity ? sku.AcceptQuantity : 0
                            }`}
                          </TableCellRowChild>
                          <TableCellRowChild align="center">
                            <ButtonGroup
                              size="small"
                              variant="text"
                              aria-label="sku action group"
                            >
                              <Tooltip title="Inventory Movement">
                                <Button
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    fontSize: 10,
                                  }}
                                  onClick={() =>
                                    handleOpenMovementModal(sku, row)
                                  }
                                >
                                  <SwapHorizontalCircle />
                                  <Box
                                    sx={{
                                      display: {
                                        xs: 'block',
                                        md: 'none',
                                      },
                                    }}
                                  >
                                    INVT MGMT
                                  </Box>
                                </Button>
                              </Tooltip>
                              <Tooltip title="Ticket History">
                                <Button
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    fontSize: 10,
                                  }}
                                  onClick={() =>
                                    handleOpenHistoryModal(sku, row)
                                  }
                                >
                                  <History />
                                  <Box
                                    sx={{
                                      display: {
                                        xs: 'block',
                                        md: 'none',
                                      },
                                    }}
                                  >
                                    TKT HST
                                  </Box>
                                </Button>
                              </Tooltip>
                            </ButtonGroup>
                          </TableCellRowChild>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            )}
          </Collapse>
        </TableCell>
      </TableRow>
      <ImageModal
        isOpen={modalOpen}
        modalClose={handleModalClose}
        skuNum={storedModalInfo.skuNum}
        description={storedModalInfo.descr}
        imgSrc={storedModalInfo.src}
      />
      {openDetailsModal ? (
        <OrderModal
          isOpen={openDetailsModal}
          handleClose={handleCloseDetailsModal}
        />
      ) : null}
      {openMoveModal ? (
        <OrderMovementModal
          isOpen={openMoveModal}
          handleClose={handleCloseMovementModal}
        />
      ) : null}
      {openHistoryModal ? (
        <OrderHistorytModal
          isOpen={openHistoryModal}
          handleClose={handleCloseHistoryModal}
        />
      ) : null}
      {openReturnProductModal ? (
        <ReturnProductModal
          isOpen={openReturnProductModal}
          handleClose={handleCloseReturnProductModal}
          submitChanges={submitReturnProductChanges}
        />
      ) : null}
      {apiResponseModal ? (
        <ApiResponseModal
          isOpen={apiResponseModal}
          title={apiResponseTitle}
          message={apiResponseMessage}
          handleClose={() => {
            setApiResponseModal(false);
            setApiResponseMessage('');
            setApiResponseTitle('');
            searchProducts();
          }}
        />
      ) : null}
    </React.Fragment>
  );
}

CollapsibleRow.propTypes = {
  row: PropTypes.shape({
    SalesOrderNumber: PropTypes.string.isRequired,
    DispatchCode: PropTypes.string,
    DispatchCodeStatus: PropTypes.number,
    WarehouseId: PropTypes.string,
    ErrorMessage: PropTypes.string,
    SkuData: PropTypes.arrayOf(
      PropTypes.shape({
        AcceptQuantity: PropTypes.number.isRequired,
        AssetId: PropTypes.string,
        OrderQuantity: PropTypes.number.isRequired,
        ReferenceColorFinish: PropTypes.string.isRequired,
        RejectQuantity: PropTypes.number.isRequired,
        SalesOrderNumber: PropTypes.string.isRequired,
        SkuNumber: PropTypes.string.isRequired,
        PMKey: PropTypes.string,
        VendorDescription: PropTypes.string.isRequired,
        returned: PropTypes.bool,
      })
    ).isRequired,
  }).isRequired,
  openOverride: PropTypes.bool.isRequired,
};
